import type { ToolUseRenderBlock } from "@/lib/chat-render";
import { toHomeRelativeOrAbsolute } from "@/lib/environment-files";

export type EntryCategory = "bash" | "search" | "read" | "edit" | "other";

export interface ViewEntry {
	key: string;
	tool: ToolUseRenderBlock;
	category: EntryCategory;
}

function entryCategory(tool: ToolUseRenderBlock): EntryCategory {
	if (tool.name === "bash") return "bash";
	if (tool.name === "glob" || tool.name === "grep") return "search";
	if (tool.name === "str_replace_based_edit_tool") {
		return tool.input.command === "view" ? "read" : "edit";
	}
	if (tool.name === "view_image" || tool.name === "view_document") return "read";
	return "other";
}

export function buildViewEntries(tools: ToolUseRenderBlock[]): ViewEntry[] {
	return tools.map((tool) => ({ key: tool.key, tool, category: entryCategory(tool) }));
}

/**
 * ファイル単位で数える。大きな文書や長いファイルは複数回に分けて読み書きするので、
 * 呼び出し回数で数えると 1 個のファイルが「5個のファイル」に見えてしまう。
 * パスは正規化してから数える。モデルは同じファイルを `spec.pdf` と `~/spec.pdf` のように
 * 書き分けるので、生の文字列で数えると分割読みが再び複数ファイルに見えてしまう。
 */
function fileCount(entries: ViewEntry[], category: EntryCategory): number {
	const files = new Set<string>();
	for (const entry of entries) {
		if (entry.category !== category) continue;
		const path = typeof entry.tool.input.path === "string" ? entry.tool.input.path.trim() : "";
		// パスを持たない起動 (入力検証エラーなど) は互いに別物として数える。
		files.add(path ? toHomeRelativeOrAbsolute(path) : entry.key);
	}
	return files.size;
}

function callCount(entries: ViewEntry[], category: EntryCategory): number {
	return entries.filter((entry) => entry.category === category).length;
}

/** 折りたたんだツールカードの見出し。Bash・検索・その他は呼び出し回数、読み書きはファイル数で数える。 */
export function summaryText(entries: ViewEntry[]): string {
	const counts: Record<EntryCategory, number> = {
		bash: callCount(entries, "bash"),
		search: callCount(entries, "search"),
		read: fileCount(entries, "read"),
		edit: fileCount(entries, "edit"),
		other: callCount(entries, "other"),
	};
	const parts: string[] = [];
	if (counts.bash) parts.push(`${counts.bash}件のBashを実行`);
	if (counts.search) parts.push(`${counts.search}回検索`);
	if (counts.read) parts.push(`${counts.read}個のファイルを読み込み`);
	if (counts.edit) parts.push(`${counts.edit}個のファイルを編集`);
	if (counts.other) parts.push(`他${counts.other}個の操作`);
	return parts.join("、") || "処理を実行";
}
